/**
 * Test Drive Controller dashboard.
 *
 * The client's role card: "Runs the demo fleet and the test drive process.
 * Captures licence and ID, controls keys, keeps demos clean, fuelled and
 * charged. Accountable for demo availability, zero lost or damaged
 * vehicles, demo cost per unit."
 *
 * The first accountability — demo availability — is the headline tile.
 * Keys/licence capture live on the Test Drives board itself (this view
 * links straight to it); cost-per-unit needs cost data the schema doesn't
 * carry yet and is an acknowledged gap, not a silent zero.
 *
 * Backend fills {@code snapshot.testDriveController} when the caller
 * carries TEST_DRIVE_CONTROLLER — see RoleSnapshotController.
 */

import { Link } from 'react-router-dom';
import KpiTile from '../../components/dashboard/KpiTile';
import { ColumnChart, DonutChart } from '../../components/dashboard/Pictorial';
import { formatInOrgTz } from '../../lib/datetime';

const fmtWhen = (iso) => formatInOrgTz(iso, {
  weekday: 'short', hour: '2-digit', minute: '2-digit',
});

export default function TestDriveControllerDashboard({ snapshot }) {
  const t = snapshot?.testDriveController;
  const noDemosFree = t && t.demosAvailableNow === 0 && t.demoFleetSize > 0;

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── ROW 1 — the fleet right now ── */}
      <div className="grid-4">
        <KpiTile index={0} label="DEMOS AVAILABLE" periodLabel="Now" to="/test-drives"
          value={t?.demosAvailableNow ?? '—'}
          color={noDemosFree ? 'var(--danger)' : 'var(--success)'}
          tone={noDemosFree ? 'danger' : undefined}
          sub={t ? `of ${t.demoFleetSize} in the demo fleet` : undefined} />
        <KpiTile index={1} label="OUT ON THE ROAD" periodLabel="Now" to="/test-drives"
          value={t?.drivesOutNow ?? '—'}
          color={(t?.drivesOutNow ?? 0) > 0 ? 'var(--warning)' : undefined}
          sub="keys out, drive in progress" />
        <KpiTile index={2} label="BOOKED TODAY" periodLabel="Today" to="/appointments"
          value={t?.drivesBookedToday ?? '—'}
          sub="test-drive appointments" />
        <KpiTile index={3} label="AWAITING CHECK-IN" periodLabel="Now" to="/test-drives"
          value={t?.drivesPlanned ?? '—'}
          sub="planned drives on the board" />
      </div>

      {/* ── ROW 2 — trade plates + day shape ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>TRADE PLATES</div>
          {t && (t.platesAvailable + t.platesInUse + t.platesExpired) === 0 ? (
            <div className="text-xs text-muted" style={{ lineHeight: 1.6 }}>
              No trade plates registered yet — add them from the Test Drives
              page so drives can go out on dealer plates.
            </div>
          ) : (
            <DonutChart centerLabel="plates" segments={[
              { label: 'Available', value: t?.platesAvailable ?? 0, color: 'var(--success)' },
              { label: 'In use',    value: t?.platesInUse ?? 0,    color: 'var(--accent-primary)' },
              { label: 'Expired',   value: t?.platesExpired ?? 0,  color: 'var(--danger)' },
            ]} />
          )}
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>THE BOARD RIGHT NOW</div>
          <ColumnChart height={160} bars={[
            { label: 'Planned',  value: t?.drivesPlanned ?? 0,    color: 'var(--info)' },
            { label: 'Out now',  value: t?.drivesOutNow ?? 0,     color: 'var(--warning)' },
            { label: 'Today',    value: t?.drivesBookedToday ?? 0, color: 'var(--accent-primary)' },
          ]} />
        </div>
      </div>

      {/* ── ROW 3 — the run sheet + fleet roster ── */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <div className="card">
            <div className="flex-between" style={{ marginBottom: 8 }}>
              <div className="card-label">UPCOMING DRIVES · RUN SHEET</div>
              <Link to="/appointments" className="text-xs" style={{ color: 'var(--accent-primary-hover)' }}>
                open diary
              </Link>
            </div>
            {(t.upcomingDrives || []).length === 0 ? (
              <div className="text-xs text-muted">
                Nothing booked from today onward — the diary is clear.
              </div>
            ) : (
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>When</th><th>Customer</th><th>Vehicle</th><th>Consultant</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {t.upcomingDrives.map((d, i) => (
                    <tr key={d.appointmentId || i}>
                      <td className="mono text-xs" style={{ fontWeight: 700 }}>{fmtWhen(d.startsAt)}</td>
                      <td>{d.customerName || '—'}</td>
                      <td>{d.vehicleLabel || <span className="text-muted">attach at check-in</span>}</td>
                      <td>{d.consultantName || '—'}</td>
                      <td>
                        <span className={`pill ${d.status === 'CONFIRMED' ? 'success' : 'info'}`}>
                          {String(d.status || '').replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="card">
            <div className="card-label" style={{ marginBottom: 8 }}>DEMO FLEET</div>
            {(t.demoFleet || []).length === 0 ? (
              <div className="text-xs text-muted">
                No demo cars in stock — the fleet roster fills in when a
                vehicle's condition is set to Demo in Inventory.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {t.demoFleet.map((v) => (
                  <div key={v.vehicleId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{v.label}</div>
                      <div className="mono text-xs text-muted">
                        {v.km != null ? `${Number(v.km).toLocaleString('en-AU')} km` : 'km not recorded'}
                      </div>
                    </div>
                    <span className={`pill filled ${v.outNow ? 'warning' : 'success'}`}>
                      {v.outNow ? 'Out' : 'On the lot'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ROW 4 — how the week went ── */}
      {t && (
        <div className="grid-4">
          <KpiTile index={0} label="COMPLETED" periodLabel="Last 7 days"
            value={t.completed7d ?? 0}
            color={(t.completed7d ?? 0) > 0 ? 'var(--success)' : undefined} />
          <KpiTile index={1} label="NO-SHOWS" periodLabel="Last 7 days"
            value={t.noShow7d ?? 0}
            color={(t.noShow7d ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
            tone={(t.noShow7d ?? 0) > 0 ? 'danger' : undefined}
            sub="booked but never arrived" />
          <KpiTile index={2} label="CANCELLED" periodLabel="Last 7 days"
            value={t.cancelled7d ?? 0} />
          <KpiTile index={3} label="PLATES EXPIRED" periodLabel="Now" to="/test-drives"
            value={t.platesExpired ?? 0}
            color={(t.platesExpired ?? 0) > 0 ? 'var(--danger)' : 'var(--success)'}
            tone={(t.platesExpired ?? 0) > 0 ? 'danger' : undefined}
            sub="renew before the next drive" />
        </div>
      )}

      {!t && (
        <div className="card">
          <div className="mono text-xs text-muted">Loading the demo fleet board…</div>
        </div>
      )}
    </div>
  );
}
